import type { Component } from 'vue'

import { REPORTING_V2_PERMISSIONS } from '@/constants'
import { createOperatorReportingCreatePrefetchGuard } from '@/views/ReportsV2/ReportExecution/guards'

type AsyncRouteComponent = Promise<{ default: Component }>

const ReportsV2 = (): AsyncRouteComponent => import('@/views/ReportsV2/Index.vue')
const ReportsV2OperatorReporting = (): AsyncRouteComponent => import('@/views/ReportsV2/OperatorReporting/Index.vue')
const ReportsV2OperatorReportingHome = (): AsyncRouteComponent =>
    import('@/views/ReportsV2/OperatorReporting/OperatorReportingHome.vue')
const ReportsV2OperatorReportingCreate = (): AsyncRouteComponent =>
    import('@/views/ReportsV2/ReportExecution/CreateReport/Index.vue')
const ReportsV2OperatorReportingCreatePrint = (): AsyncRouteComponent =>
    import('@/views/ReportsV2/ReportExecution/CreatePrintReport/Index.vue')
const ReportsV2ReportExecution = (): AsyncRouteComponent => import('@/views/ReportsV2/ReportExecution/Index.vue')
const ReportsV2ReportExecutionDetail = (): AsyncRouteComponent =>
    import('@/views/ReportsV2/ReportExecution/ActiveReport/Index.vue')
const ReportsV2ManagerApprovals = (): AsyncRouteComponent => import('@/views/ReportsV2/ManagerApprovals/Index.vue')
const ReportsV2ManagerApprovalsList = (): AsyncRouteComponent => import('@/views/ReportsV2/ManagerApprovals/ManagerApprovals.vue')
const ReportsV2ManagerApprovalsDetailedList = (): AsyncRouteComponent =>
    import('@/views/ReportsV2/ManagerApprovals/DetailedReports.vue')
const ReportsV2MyReports = (): AsyncRouteComponent => import('@/views/ReportsV2/MyReports/Index.vue')
const ReportsV2MyReportsList = (): AsyncRouteComponent => import('@/views/ReportsV2/MyReports/MyReports.vue')
const ReportsV2MyReportsDetailedList = (): AsyncRouteComponent => import('@/views/ReportsV2/MyReports/DetailedReports.vue')
const ReportsV2PaperReporting = (): AsyncRouteComponent => import('@/views/ReportsV2/PaperReporting/Index.vue')
const ReportsV2PaperReportingDetail = (): AsyncRouteComponent =>
    import('@/views/ReportsV2/PaperReporting/DetailedPaperReports.vue')
const ReportsV2PaperReportingList = (): AsyncRouteComponent => import('@/views/ReportsV2/PaperReporting/PaperReporting.vue')
const ReportsV2Prioritization = (): AsyncRouteComponent => import('@/views/ReportsV2/Prioritization/Index.vue')
const ReportsV2PrioritizationList = (): AsyncRouteComponent => import('@/views/ReportsV2/Prioritization/Prioritization.vue')

export default [
    {
        path: '/reports-v2',
        name: 'reportsV2',
        component: ReportsV2,
        meta: {
            title: 'reports.rootV2.pageTitle',
            menuRendered: true,
            menuTitle: 'reports.rootV2.menuTitle',
            icon: 'mdi-clock-outline',
            requiresAuth: true,
            permission: REPORTING_V2_PERMISSIONS.ACCESS
        },
        children: [
            {
                path: '/reports-v2/reports-shift-summaries',
                name: 'reportsV2ManagerApprovals',
                component: ReportsV2ManagerApprovals,
                meta: {
                    title: 'reports.managerApprovals.routeTitle',
                    menuRendered: true,
                    menuTitle: 'reports.managerApprovals.menuTitle',
                    requiresAuth: true,
                    permission: 'reports_review'
                },
                redirect: { name: 'reportsV2ManagerApprovalsList' },
                children: [
                    {
                        path: '',
                        name: 'reportsV2ManagerApprovalsList',
                        component: ReportsV2ManagerApprovalsList,
                        meta: {
                            title: 'reports.managerApprovals.routeTitle',
                            requiresAuth: true,
                            permission: 'reports_review'
                        }
                    },
                    {
                        path: ':id',
                        name: 'reportsV2ManagerApprovalsDetailedReports',
                        component: ReportsV2ManagerApprovalsDetailedList,
                        meta: {
                            title: 'reports.managerApprovals.detailed.routeTitle',
                            requiresAuth: true,
                            permission: 'reports_review'
                        }
                    }
                ]
            },
            {
                path: '/reports-v2/reporting',
                name: 'reportsV2OperatorReporting',
                component: ReportsV2OperatorReporting,
                meta: {
                    title: 'reports.operatorReporting.routeTitle',
                    menuRendered: true,
                    menuTitle: 'reports.operatorReporting.menuTitle',
                    requiresAuth: true,
                    permission: REPORTING_V2_PERMISSIONS.ACCESS
                },
                redirect: { name: 'reportsV2OperatorReportingHome' },
                children: [
                    {
                        path: '',
                        name: 'reportsV2OperatorReportingHome',
                        component: ReportsV2OperatorReportingHome,
                        meta: {
                            title: 'reports.operatorReporting.routeTitle',
                            requiresAuth: true,
                            permission: REPORTING_V2_PERMISSIONS.ACCESS
                        }
                    },
                    {
                        path: 'create',
                        name: 'reportsV2OperatorReportingCreate',
                        component: ReportsV2OperatorReportingCreate,
                        beforeEnter: createOperatorReportingCreatePrefetchGuard(),
                        meta: {
                            title: 'reports.operatorReporting.createRouteTitle',
                            requiresAuth: true,
                            permission: REPORTING_V2_PERMISSIONS.CREATE
                        }
                    },
                    {
                        path: 'print-create',
                        name: 'reportsV2OperatorReportingCreatePrint',
                        component: ReportsV2OperatorReportingCreatePrint,
                        beforeEnter: createOperatorReportingCreatePrefetchGuard(),
                        meta: {
                            title: 'reports.operatorReporting.createRouteTitle',
                            requiresAuth: true,
                            permission: REPORTING_V2_PERMISSIONS.CREATE
                        }
                    }
                ]
            },
            {
                path: '/reports-v2/report-execution',
                name: 'reportsV2ReportExecution',
                component: ReportsV2ReportExecution,
                meta: {
                    title: 'reports.reportExecution.detail.routeTitle',
                    requiresAuth: true,
                    permission: REPORTING_V2_PERMISSIONS.ACCESS
                },
                children: [
                    {
                        path: ':id',
                        name: 'reportsV2ReportExecutionDetail',
                        component: ReportsV2ReportExecutionDetail,
                        meta: {
                            title: 'reports.reportExecution.detail.routeTitle',
                            requiresAuth: true,
                            permission: REPORTING_V2_PERMISSIONS.ACCESS
                        }
                    }
                ]
            },
            {
                path: '/reports-v2/prioritization',
                name: 'reportsV2Prioritization',
                component: ReportsV2Prioritization,
                meta: {
                    title: 'reports.prioritization.routeTitle',
                    menuRendered: true,
                    menuTitle: 'reports.prioritization.menuTitle',
                    requiresAuth: true,
                    permission: 'schedule_prioritization'
                },
                redirect: { name: 'reportsV2PrioritizationList' },
                children: [
                    {
                        path: '/reports-v2/prioritization',
                        name: 'reportsV2PrioritizationList',
                        component: ReportsV2PrioritizationList,
                        meta: {
                            title: 'reports.prioritization.routeTitle',
                            requiresAuth: true,
                            permission: 'schedule_prioritization'
                        }
                    }
                ]
            },
            {
                path: '/reports-v2/paper-reporting',
                name: 'reportsV2PaperReporting',
                component: ReportsV2PaperReporting,
                meta: {
                    title: 'reports.paperReporting.routeTitle',
                    menuRendered: true,
                    menuTitle: 'reports.paperReporting.menuTitle',
                    requiresAuth: true,
                    permission: 'paper_report'
                },
                redirect: { name: 'reportsV2PaperReportingList' },
                children: [
                    {
                        path: '',
                        name: 'reportsV2PaperReportingList',
                        component: ReportsV2PaperReportingList,
                        meta: {
                            title: 'reports.paperReporting.routeTitle',
                            requiresAuth: true,
                            permission: 'paper_report'
                        }
                    },
                    {
                        path: ':id',
                        name: 'reportsV2PaperReportingDetail',
                        component: ReportsV2PaperReportingDetail,
                        meta: {
                            title: 'reports.paperReporting.detail.routeTitle',
                            requiresAuth: true,
                            permission: REPORTING_V2_PERMISSIONS.ACCESS
                        }
                    }
                ]
            },
            {
                path: '/reports-v2/user-reports-shift-summaries',
                name: 'reportsV2MyReports',
                component: ReportsV2MyReports,
                meta: {
                    title: 'reports.myReports.routeTitle',
                    menuRendered: true,
                    menuTitle: 'reports.myReports.menuTitle',
                    requiresAuth: true,
                    permission: REPORTING_V2_PERMISSIONS.ACCESS
                },
                redirect: { name: 'reportsV2MyReportsList' },
                children: [
                    {
                        path: '',
                        name: 'reportsV2MyReportsList',
                        component: ReportsV2MyReportsList,
                        meta: {
                            title: 'reports.myReports.routeTitle',
                            requiresAuth: true,
                            permission: REPORTING_V2_PERMISSIONS.ACCESS
                        }
                    },
                    {
                        path: ':id',
                        name: 'reportsV2MyReportsDetailedReports',
                        component: ReportsV2MyReportsDetailedList,
                        meta: {
                            title: 'reports.myReports.detailed.routeTitle',
                            requiresAuth: true,
                            permission: REPORTING_V2_PERMISSIONS.ACCESS
                        }
                    }
                ]
            }
        ]
    }
]
